import React, { useEffect, useState } from 'react';
import AppCard from './AppCard';
import AddAppModal from './AddAppModal';
import SettingsModal from './SettingsModal';
import Window from './Window';
import AppDrawer from './AppDrawer';
import Terminal from './Terminal';
import ContextMenu from './ContextMenu';
import SitesPanel from './SitesPanel';
import CreatorFooter from './CreatorFooter';
import WelcomeModal from './WelcomeModal';
import DesktopLogin from './DesktopLogin';
import { loadAuthConfig, hasRight } from '../utils/auth';
import { initializeSites, getSites, addSite, updateSite, deleteSite } from '../utils/sites';
import { subscribe as subscribeNotify, notify } from '../utils/notify';

const STORAGE_KEY = 'my-dashboard-apps';

export default function Dashboard() {
  const [apps, setApps] = useState([]);
  const [sites, setSites] = useState([]);
  const [filter, setFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingType, setEditingType] = useState('app'); // 'app' or 'site'
  const [settings, setSettings] = useState({ theme: 'light', density: 'comfortable', showIcons: true });
  const [windows, setWindows] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(!!sessionStorage.getItem('my-dashboard-authenticated'));
  const [showLinuxLogin, setShowLinuxLogin] = useState(true); // Force login at startup
  const [currentUser, setCurrentUser] = useState(sessionStorage.getItem('my-dashboard-user') || '');
  const [userRights, setUserRights] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showSitesPanel, setShowSitesPanel] = useState(() => {
    const saved = localStorage.getItem('dashboard-show-sites');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const zRef = React.useRef(200);

  // Check if this is first visit
  useEffect(() => {
    const hasVisited = localStorage.getItem('my-dashboard-visited');
    if (!hasVisited) {
      setShowWelcome(true);
      localStorage.setItem('my-dashboard-visited', 'true');
    }
  }, []);

  // Subscribe to notifications
  useEffect(() => {
    const unsub = subscribeNotify(n => setNotifications(prev => [n, ...prev].slice(0,6)));
    return () => unsub();
  }, []);

  // Notification de connexion utilisateur
  useEffect(() => {
    if (isAuthenticated && currentUser) {
      notify('info', `✓ Connecté: ${currentUser}`);
    }
  }, [isAuthenticated, currentUser]);

  function dismissNotification(id){ setNotifications(prev => prev.filter(n=>n.id!==id)); }

  useEffect(() => {
    try {
      const raw = localStorage.getItem('my-dashboard-settings');
      if (raw) setSettings(JSON.parse(raw));
    } catch (e) { console.warn('no settings', e); }
  }, []);

  // Load auth config and determine if login is required
  useEffect(() => {
    try {
      const cfg = loadAuthConfig();
      if (cfg && cfg.enabled) {
        setAuthRequired(true);
        if (!sessionStorage.getItem('my-dashboard-authenticated')) {
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
          setShowLinuxLogin(false);
        }
      } else {
        // even if no custom auth set, show Linux login for admin
        setAuthRequired(true);
      }
    } catch (e) { console.warn('auth load failed', e); }
  }, []);

  // Load user rights when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const user = sessionStorage.getItem('my-dashboard-user');
      setCurrentUser(user || '');
      if (user) {
        // ymnode (admin) has all rights by default
        if (user === 'ymnode') {
          setUserRights(['addSite', 'removeSite', 'editSite', 'manageUsers']);
        } else {
          // Other users: check their rights from the system
          const userRights = [];
          if (hasRight(user, 'addSite')) userRights.push('addSite');
          if (hasRight(user, 'removeSite')) userRights.push('removeSite');
          if (hasRight(user, 'editSite')) userRights.push('editSite');
          if (hasRight(user, 'manageUsers')) userRights.push('manageUsers');
          setUserRights(userRights);
        }
      }
    }
  }, [isAuthenticated]);

  // If user changes settings to force login on load, ensure login modal shows
  useEffect(() => {
    try {
      if (settings && settings.forceLogin && authRequired && !isAuthenticated) {
        setShowLinuxLogin(true);
      }
    } catch (e) {}
  }, [settings.forceLogin, authRequired, isAuthenticated]);

  // Apply theme to body
  useEffect(() => {
    if (settings.theme === 'dark') {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    localStorage.setItem('my-dashboard-settings', JSON.stringify(settings));
  }, [settings]);

  function logout() {
    sessionStorage.removeItem('my-dashboard-authenticated');
    sessionStorage.removeItem('my-dashboard-user');
    setIsAuthenticated(false);
    setCurrentUser('');
    setUserRights([]);
    setShowLinuxLogin(true);
  }

  // Apply user wallpaper if one was saved
  useEffect(() => {
    try {
      const w = localStorage.getItem('my-dashboard-wallpaper');
      if (w) {
        document.documentElement.style.setProperty('--wallpaper-url', `url('${w}')`);
      } else {
        document.documentElement.style.setProperty('--wallpaper-url', "url('/wallpaper-linux.jpg')");
      }
    } catch (e) {
      console.warn('Failed to apply wallpaper', e);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('my-dashboard-settings', JSON.stringify(settings));
    document.documentElement.classList.remove('theme-icloud','theme-linux','theme-dark');
    document.documentElement.classList.add(`theme-${settings.theme || 'icloud'}`);
  }, [settings]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(apps));
  }, [apps]);

  // Seed some example apps if empty (desktop feel)
  useEffect(() => {
    if (apps.length === 0) {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        const samples = [
          { id: 'heimdall', title: 'Heimdall', url: 'https://heimdall.local/' },
          { id: 'dashy', title: 'Dashy', url: 'https://dashy.local/' },
          { id: 'portainer', title: 'Portainer', url: 'https://portainer.local/' },
          { id: 'grafana', title: 'Grafana', url: 'https://grafana.local/' }
        ];
        setApps(samples);
      }
    }
  }, [apps.length]);

  // try to load persisted apps metadata
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setApps(JSON.parse(raw));
    } catch(e){}
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setApps(JSON.parse(raw));
    } catch (e) {
      console.error('Failed to load apps', e);
    }
  }, []);

  // Initialize sites from static config file on app startup
  useEffect(() => {
    const loadSites = async () => {
      await initializeSites().catch(e => console.warn('Failed to initialize sites:', e));
      // Load sites from localStorage
      try {
        const loadedSites = getSites();
        setSites(loadedSites);
      } catch (e) {
        console.warn('Failed to load sites:', e);
      }
    };
    loadSites();
  }, []);

  // Keyboard shortcuts (Super key for app drawer, etc.)
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Meta' || (e.ctrlKey && e.key === 'a')) { // Super or Ctrl+A
        setDrawerOpen(true);
        e.preventDefault();
      }
      if (e.ctrlKey && e.key === '`') { // Ctrl+` for terminal
        setTerminalOpen(true);
        e.preventDefault();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    function handleResize() { setIsMobile(window.innerWidth <= 768); }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(apps));
  }, [apps]);

  function handleAdd(app) {
    setApps(prev => [app, ...prev]);
    setModalOpen(false);
    setEditingType('app');
  }

  function handleUpdate(updated) {
    setApps(prev => prev.map(a => (a.id === updated.id ? updated : a)));
    setEditing(null);
    setModalOpen(false);
    setEditingType('app');
  }

  function handleRemove(id) {
    setApps(prev => prev.filter(a => a.id !== id));
  }

  // Wrapper function to handle both apps and sites
  function handleAddOrUpdate(item) {
    if (editingType === 'site') {
      if (editing && editing.id) {
        handleUpdateSite(item);
      } else {
        handleAddSite(item);
      }
    } else {
      if (editing && editing.id) {
        handleUpdate(item);
      } else {
        handleAdd(item);
      }
    }
  }

  // Sites management functions
  function handleAddSite(site) {
    const newSite = addSite({
      name: site.title || site.name,
      url: site.url,
      category: site.category || 'General'
    });
    setSites(prev => [newSite, ...prev]);
    setModalOpen(false);
    setEditingType('app');
  }

  function handleUpdateSite(updated) {
    updateSite(updated.id, {
      name: updated.title || updated.name,
      url: updated.url,
      category: updated.category || 'General'
    });
    setSites(prev => prev.map(s => (s.id === updated.id ? updated : s)));
    setEditing(null);
    setModalOpen(false);
    setEditingType('app');
  }

  function handleRemoveSite(id) {
    deleteSite(id);
    setSites(prev => prev.filter(s => s.id !== id));
  }

  function openEdit(app) {
    setEditing(app);
    setModalOpen(true);
  }

  function openWindow(app) {
    // enforce auth if required
    if (authRequired && !isAuthenticated) {
      setShowLinuxLogin(true);
      return;
    }
    const id = `w-${app.id}-${Date.now()}`;
    const w = { id, appId: app.id, title: app.title || app.name, url: app.url, x: 140, y: 120, w: 900, h: 600, z: ++zRef.current };
    setWindows(prev => [...prev, w]);
  }

  function closeWindow(id) { setWindows(prev => prev.filter(w => w.id !== id)); }

  function focusWindow(id) { setWindows(prev => prev.map(w => w.id === id ? {...w, z: ++zRef.current} : w)); }

  // dock reorder handlers
  function onDockDragStart(e, index) {
    e.dataTransfer.setData('text/plain', index.toString());
  }

  function onDockDrop(e, index) {
    e.preventDefault();
    const from = Number(e.dataTransfer.getData('text/plain'));
    if (isNaN(from)) return;
    const copy = [...apps];
    const [item] = copy.splice(from,1);
    copy.splice(index,0,item);
    setApps(copy);
  }

  function onDockDragOver(e){ e.preventDefault(); }

  // --- Sites panel helpers: try HEAD fetch to the app URL first, then fallback to favicon ---
  function pingApp(app) {
    const timeoutMs = 2500;
    // Try a HEAD request to the app's root URL (if present). This often gives the most accurate online status.
    const tryFetch = () => new Promise(async (resolve) => {
      if (!app.url) return resolve({ ok: false, reason: 'no-url' });
      try {
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeoutMs);
        // Use HEAD to be lightweight. Some servers don't support HEAD; try GET as fallback if HEAD fails.
        let res;
        try {
          res = await fetch(app.url, { method: 'HEAD', signal: controller.signal });
        } catch (e) {
          // fallback to GET if HEAD is not allowed or fails quickly
          try {
            res = await fetch(app.url, { method: 'GET', signal: controller.signal });
          } catch (e2) {
            clearTimeout(id);
            return resolve({ ok: false, reason: e2 });
          }
        }
        clearTimeout(id);
        resolve({ ok: res && res.ok, status: res && res.status });
      } catch (e) {
        resolve({ ok: false, reason: e });
      }
    });

    // Fallback: try loading the favicon as an Image (works around some CORS/HEAD restrictions)
    const tryFavicon = () => new Promise(resolve => {
      const url = app.icon || (app.url ? (new URL(app.url).origin + '/favicon.ico') : '/favicon.ico');
      const img = new Image();
      const t = setTimeout(() => { img.onload = img.onerror = null; resolve({ ok: false, via: 'favicon', status: 'unknown' }); }, 3000);
      img.onload = () => { clearTimeout(t); resolve({ ok: true, via: 'favicon', status: 200 }); };
      img.onerror = () => { clearTimeout(t); resolve({ ok: false, via: 'favicon', status: 404 }); };
      img.src = url + (url.includes('?') ? '&' : '?') + 'v=' + Date.now();
    });

    return new Promise(async (resolve) => {
      try {
        const fetched = await tryFetch();
        if (fetched && fetched.ok) {
          return resolve({ status: String(fetched.status || 200), checked: Date.now(), via: 'fetch' });
        }
      } catch (e) {
        // ignore and fallback
      }

      // fallback to favicon
      const fav = await tryFavicon();
      if (fav.ok) return resolve({ status: String(fav.status || 200), checked: Date.now(), via: 'favicon' });
      return resolve({ status: 'offline', checked: Date.now(), via: fav.via || 'favicon' });
    });
  }

  async function checkSite(appId) {
    const app = apps.find(a=>a.id===appId);
    if (!app) return;
    const res = await pingApp(app);
    setApps(prev => prev.map(a => a.id===appId ? {...a, lastChecked: res.checked, status: res.status, statusVia: res.via || 'unknown'} : a));
  }

  async function checkAll() {
    for (const a of apps) {
      // run checks with small delay to avoid flooding
      // eslint-disable-next-line no-await-in-loop
      await checkSite(a.id);
    }
  }

  const visible = [...apps, ...sites].filter(a => {
    const title = (a.title || a.name || '').toLowerCase();
    const url = (a.url || '').toLowerCase();
    return title.includes(filter.toLowerCase()) || url.includes(filter.toLowerCase());
  });

  function handleContextMenu(e, target) {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, target });
  }

  // Show login screen first if not authenticated
  if (showLinuxLogin) {
    return <DesktopLogin open={true} onSuccess={() => { setIsAuthenticated(true); setShowLinuxLogin(false); }} onCancel={() => {}} />;
  }

  return (
    <div className="dashboard-root" onContextMenu={(e) => handleContextMenu(e, null)}>
      <div className="topbar">
        <div className="top-left">
          <div className="system-dot" />
          <div className="system-dot yellow" />
          <div className="system-dot green" />
          <div className="top-title">Ilhan Dashboard</div>
        </div>
        <div className="top-center">
          <input className="search top-search" placeholder="Rechercher..." value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
        <button className="hamburger-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <span></span>
          <span></span>
          <span></span>
        </button>
        <div className="top-right">
          <div className="clock">{new Date().toLocaleTimeString()}</div>
          <button className="btn" title="Applications (Super)" onClick={() => { if (authRequired && !isAuthenticated) { setShowLinuxLogin(true); } else setDrawerOpen(true); }}>⊞</button>
          <button className="btn" title="Terminal (Ctrl+`)" onClick={() => setTerminalOpen(true)}>›_</button>
          <button className="btn" title={showSitesPanel ? "Masquer sites" : "Afficher sites"} onClick={() => { setShowSitesPanel(!showSitesPanel); localStorage.setItem('dashboard-show-sites', !showSitesPanel); }}>🌐</button>
          <button className="btn" title="Paramètres" onClick={() => setSettings(s => ({...s, _open:true}))}>⚙︎</button>
          {authRequired && isAuthenticated && (
            <button className="btn" title="Déconnexion" onClick={logout}>⎋</button>
          )}
        </div>
      </div>

      {!isMobile && (
        <aside className="dock" aria-hidden>
          {userRights.includes('addSite') && (
            <button
              className="dock-item add-btn liquid-plus"
              title="Ajouter une application"
              onClick={() => { setEditing(null); setEditingType('app'); setModalOpen(true); }}
            >
              +
            </button>
          )}
          {/* Display apps and sites in dock */}
          {[...apps, ...sites].slice(0,8).map((a, idx) => (
            <button
              key={a.id}
              className="dock-item"
              title={a.title || a.name}
              draggable
              onDragStart={(e)=> onDockDragStart(e, idx)}
              onDragOver={onDockDragOver}
              onDrop={(e)=> onDockDrop(e, idx)}
              onClick={() => openWindow(a)}
            >
              <img src={a.icon || (a.url ? (new URL(a.url).origin + '/favicon.ico') : '/favicon.ico')} alt="dock" onError={(e)=> e.currentTarget.src='/favicon.ico'} />
            </button>
          ))}
        </aside>
      )}

      {/* Mobile menu overlay and menu */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={() => setMobileMenuOpen(false)} />
      <nav className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-item" onClick={() => { setDrawerOpen(true); setMobileMenuOpen(false); }}>
          📱 Applications
        </div>
        <div className="mobile-menu-item" onClick={() => { setTerminalOpen(true); setMobileMenuOpen(false); }}>
          ›_ Terminal
        </div>
        <div className="mobile-menu-item" onClick={() => { setShowSitesPanel(!showSitesPanel); localStorage.setItem('dashboard-show-sites', !showSitesPanel); setMobileMenuOpen(false); }}>
          🌐 {showSitesPanel ? 'Masquer' : 'Afficher'} sites
        </div>
        <div className="mobile-menu-item" onClick={() => { setSettings(s => ({...s, _open:true})); setMobileMenuOpen(false); }}>
          ⚙︎ Paramètres
        </div>
        {authRequired && isAuthenticated && (
          <>
            <div className="mobile-menu-item divider" />
            <div className="mobile-menu-item" onClick={() => { logout(); setMobileMenuOpen(false); }}>
              ⎋ Déconnexion
            </div>
          </>
        )}
        {userRights.includes('addSite') && (
          <>
            <div className="mobile-menu-item divider" />
            <div className="mobile-menu-item" onClick={() => { setEditing(null); setEditingType('app'); setModalOpen(true); setMobileMenuOpen(false); }}>
              ➕ Ajouter une app
            </div>
          </>
        )}
      </nav>      {/* Notifications */}
      <div className="notifications-root" style={{position:'fixed',right:12,top:12,zIndex:9999}}>
        {notifications.map(n => (
          <div key={n.id} style={{background: n.type==='error' ? 'rgba(255,80,80,0.95)' : 'rgba(0,120,255,0.95)',color:'#fff',padding:'8px 12px',borderRadius:8,boxShadow:'0 6px 18px rgba(0,0,0,0.25)',marginBottom:8}}>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <div style={{flex:1}}>{n.message}</div>
              <button style={{background:'transparent',border:'none',color:'#fff',cursor:'pointer'}} onClick={()=> dismissNotification(n.id)}>✕</button>
            </div>
          </div>
        ))}
      </div>

      {showSitesPanel && (
        <SitesPanel
          apps={sites}
          onEdit={(app) => { setEditing(app); setEditingType('site'); setModalOpen(true); }}
          onRemove={handleRemoveSite}
          onOpenWindow={openWindow}
          onCheck={checkSite}
          onCheckAll={checkAll}
        />
      )}

      <main className={`dashboard-main ${isMobile ? 'mobile' : 'desktop'} ${settings.theme === 'linux' ? 'linux-mode' : ''}`}>
        <section className="grid">
          {visible.length === 0 && (
            <div className="empty">Aucune application, cliquez sur "Ajouter" pour en créer une.</div>
          )}
          {visible.map(app => (
            <AppCard key={app.id} app={app} onEdit={() => openEdit(app)} onRemove={() => handleRemove(app.id)} compact={settings.density === 'compact'} showIcons={settings.showIcons} onOpenWindow={() => openWindow(app)} />
          ))}
        </section>
      </main>

      <AddAppModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingType('app'); }}
        onAdd={handleAddOrUpdate}
        onUpdate={handleAddOrUpdate}
        editing={editing}
      />

      <SettingsModal
        open={!!settings._open}
        onClose={() => setSettings(s => ({...s, _open:false}))}
        settings={settings}
        onChange={(s)=> setSettings({...s, _open:false})}
        onImportApps={(arr)=> setApps(arr)}
        onExportApps={()=>{ 
          const data = { apps, sites, exportedAt: new Date().toISOString() };
          const json = JSON.stringify(data, null, 2);
          const blob = new Blob([json], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `dashboard-backup-${Date.now()}.json`;
          a.click();
          URL.revokeObjectURL(url);
          alert('Sauvegarde téléchargée');
        }}
        onResetApps={()=> setApps([])}
      />

      {/* Window instances */}
      {windows.map(w => (
        <Window key={w.id} id={w.id} title={w.title} url={w.url} zIndex={w.z || 200} initial={{x:w.x,y:w.y,w:w.w,h:w.h}} onClose={closeWindow} onFocus={focusWindow} />
      ))}
      <AppDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} apps={apps} onOpenWindow={openWindow} />
      
      <Terminal open={terminalOpen} onClose={() => setTerminalOpen(false)} apps={[...apps, ...sites]} />

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={[
            { label: 'Ajouter une app', icon: '+', action: () => { setEditing(null); setModalOpen(true); } },
            { label: 'Terminal', icon: '›_', action: () => setTerminalOpen(true) },
            { label: 'Paramètres', icon: '⚙', action: () => setSettings(s => ({...s, _open:true})) }
          ]}
          onClose={() => setContextMenu(null)}
        />
      )}

      {settings.name && (
        <div className="greeting-bar">Bonjour, {settings.name} 👋</div>
      )}

      <CreatorFooter />

      {showWelcome && <WelcomeModal onClose={() => setShowWelcome(false)} />}
    </div>
  );
}

